import Axios from 'axios';
import {
  deleteVideoMutation,
  createVideoMutation,
  removeEngageVideoMutation,
  addEngageVideoMutation,
  copyVideoMutation,
  updateVideoMutation,
} from '@/api/video/graphql/video.mutations';
import { VideoQueries } from '@/api/video/graphql/video.queries';
import { IVideo } from '@/types/Video';
import { ICreateVideoDTO } from '@/api/video/dtos/create-video-dto';
import { apolloClient } from '@/apollo';
import { IVideoLibraryResponseDTO } from '@/api/video/dtos/video-library-response-dto';
import { EVideoSource } from '@/video/types/enums/video-source';
import { VideoHelper } from '@/video/helpers/video.helper';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';
import { IUpdateVideoPayload } from '@/video/engagement/types/update-video-payload';

export class VideoService {
  static async loadVideos(
    offset: number,
    limit: number,
    saved: boolean,
    search = ''
  ): Promise<IVideoLibraryResponseDTO> {
    const {
      data: { videos },
    } = await apolloClient.query({
      query: VideoQueries.videosQuery,
      variables: { search, offset, limit, saved },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return VideoHelper.correctDates(videos);
  }

  static async loadVideo(id: string): Promise<IVideo> {
    const {
      data: { video },
    } = await apolloClient.query({
      query: VideoQueries.videoQuery,
      variables: { id },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return video;
  }

  static async loadThumbnail(id: string): Promise<Partial<IVideo>> {
    const {
      data: { video },
    } = await apolloClient.query({
      query: VideoQueries.videoThumbnail,
      variables: { id },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return video;
  }

  static async deleteVideo(id: string) {
    const { data } = await apolloClient.mutate({
      mutation: deleteVideoMutation,
      variables: { id },
    });
    return data;
  }

  static async createVideo({
    videoTitle,
    published,
    saved,
    thumbUrl,
    videoUrl,
    videoType,
  }: ICreateVideoDTO): Promise<{ video: IVideo; uploadLink: string }> {
    const {
      data: { createVideo },
    } = await apolloClient.mutate({
      mutation: createVideoMutation,
      variables: {
        videoTitle,
        published,
        saved,
        thumbUrl,
        videoUrl,
        videoType,
      },
    });
    return createVideo;
  }

  static async createVideoLink(
    videoUrl: string,
    videoTitle: string,
    videoType: EVideoSource
  ): Promise<IVideo> {
    const {
      data: { createVideo },
    } = await apolloClient.mutate({
      mutation: createVideoMutation,
      variables: {
        videoTitle,
        published: false,
        saved: false,
        videoUrl,
        videoType,
      },
    });
    return createVideo.video;
  }

  static async uploadVideo({
    file,
    uploadLink,
  }: {
    file: File;
    uploadLink: string;
  }): Promise<void> {
    await Axios.put(uploadLink, file);
  }

  static async uploadVideoDone(videoId: string): Promise<string> {
    const {
      data: {
        uploadVideoDone: { videoUrl },
      },
    } = await apolloClient.query({
      query: VideoQueries.uploadVideoDoneQuery,
      variables: {
        videoId,
      },
    });
    return videoUrl;
  }

  static async addEngageVideo(videoId: string, documentId: string): Promise<boolean> {
    return !!(await apolloClient.mutate({
      mutation: addEngageVideoMutation,
      variables: { videoId, documentId },
    }));
  }

  static async removeEngageVideo(videoId: string, documentId: string): Promise<boolean> {
    return !!(await apolloClient.mutate({
      mutation: removeEngageVideoMutation,
      variables: { videoId, documentId },
    }));
  }

  static async loadEngageVideo(documentId: string): Promise<IVideo> {
    const {
      data: {
        document: {
          video: { video },
        },
      },
    } = await apolloClient.query({
      query: VideoQueries.engageVideoQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NoCache,
    });
    return video;
  }

  static async copyVideo(id: string): Promise<IVideo> {
    const {
      data: { copyVideo },
    } = await apolloClient.mutate({
      mutation: copyVideoMutation,
      variables: {
        id,
      },
    });
    return copyVideo;
  }

  static async loadEngageVideos(): Promise<IVideo[]> {
    const {
      data: { video },
    } = await Axios.get('/get/video/list');
    return video.map(video => ({
      videoId: video.video_id,
      thumbUrl: video.thumb_url,
      videoTitle: video.video_title,
      videoType: video.video_type,
      videoUrl: video.video_url,
      published: video.published,
    }));
  }

  static async deleteEngageVideo(videoId: string): Promise<void> {
    const body = new FormData();
    body.append('video-id', videoId);
    const { data } = await Axios.post('/get/video/delete', body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
  }

  static async updateVideo(payload: IUpdateVideoPayload): Promise<void> {
    await apolloClient.mutate({
      mutation: updateVideoMutation,
      variables: {
        id: payload.videoId,
        videoTitle: payload.videoTitle,
        published: payload.published,
      },
    });
  }
}
